import { DeviceCategory, DeviceSubCategory } from '@eppendorf/app-configs';
import { EventSeverity } from '@eppendorf/vnls-notifications-utils';
import {
  Button,
  CardSelect,
  Checkbox,
  Dialog,
  DotsLoader,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useGetDeviceCategoriesList } from '$components/app-config/app-config.api';
import { useDeviceModels } from '$components/app-config/useDeviceModels';
import { useFeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { useDevicesWithQueryParams } from '$features/devices/devices.api';
import { getEventIconName } from '$features/monitoring/events/events.utils';
import {
  getDeviceCategoriesQuery,
  localizeDeviceCategory,
} from '$features/notifications/notification-category.utils';
import {
  AddNotificationRuleScreenProps,
  NotificationRuleConditionMode,
  Severity,
} from '$features/notifications/types';

import { NotificationCategoryCard } from './components/notification-category-card';
import {
  isRangeCategory,
  MonitoringDeviceLink,
  NotificationRangeCategoryCard,
} from './components/notification-range-category-card';
import './condition-screen.scss';

interface SeverityListItemProps {
  severity: Severity;
  selected: boolean;
  onSelect: (severity: Severity) => void;
}

function SeverityListItem({ severity, selected, onSelect }: SeverityListItemProps) {
  const { name } = severity;
  const { t } = useTranslation();
  const message = t(`notificationTags.${name}Message` as const);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={`condition-severity-card${selected ? ' condition-severity-card--selected' : ''}`}
      onClick={() => onSelect(severity)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(severity);
        }
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- prevent the checkbox click from toggling the card twice */}
      <span onClick={(event) => event.stopPropagation()}>
        <Checkbox
          id={`condition-severity-${name}`}
          checked={selected}
          onChange={() => onSelect(severity)}
          ariaLabel={severity.name}
          className="p-m"
        />
      </span>
      <Icon
        name={`event-${getEventIconName(name)}`}
        size={IconSizeClasses.XSmall}
        key={`tag-${name}`}
        className="m-x-m p-x-m"
      />
      <span className="p-top-xxs">
        <strong className="font-weight-bold">
          {t(`notificationTags.${name}` as const)}{' '}
        </strong>
        {message}
      </span>
    </div>
  );
}

function SeverityList({ formControl }: AddNotificationRuleScreenProps) {
  const { t } = useTranslation();
  const { watch, setValue } = formControl;
  const selectedTags = watch('tags') || [];

  const handleSeveritySelect = useCallback(
    (severity: EventSeverity) => {
      const tags = watch('tags') || [];
      if (tags.includes(severity)) {
        setValue(
          'tags',
          tags.filter((tag) => tag !== severity),
          { shouldValidate: true, shouldDirty: true },
        );
      } else {
        setValue('tags', [...tags, severity], {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    [watch, setValue],
  );

  return (
    <>
      <p className="p-bottom-xxl">
        <span>{t('tagsCondition.notify')}</span>
        <strong className="font-weight-bold">{`${t('tagsCondition.events')} `}</strong>
        <span>{t('tagsCondition.within')}</span>
        <strong className="font-weight-bold">{t('tagsCondition.severity')}</strong>
      </p>
      <div className="flex flex__dir--column gap-l">
        {Object.values(EventSeverity).map((severity) => (
          <SeverityListItem
            key={severity}
            severity={{ name: severity }}
            onSelect={() => handleSeveritySelect(severity)}
            selected={selectedTags.includes(severity)}
          />
        ))}
      </div>
      {selectedTags.includes(EventSeverity.INFO) && (
        <div className="condition-screen__severity-warning" role="status">
          <Icon name="event-warning" size={IconSizeClasses.XSmall} />
          <p>
            <Trans
              i18nKey="tagsCondition.highVolumeWarning"
              components={{ bold: <strong className="font-weight-bold" /> }}
            />
          </p>
        </div>
      )}
    </>
  );
}

const DEVICE_PAGE_INDEX = 1;
const DEVICE_PAGE_SIZE = 500;

interface AdvancedConditionsProps extends AddNotificationRuleScreenProps {
  categoriesQuery: ReturnType<typeof useGetDeviceCategoriesList>;
}

function AdvancedConditions({ formControl, categoriesQuery }: AdvancedConditionsProps) {
  const { t } = useTranslation();
  const { getValues, setValue, watch } = formControl;
  const selectedDevices = watch('devices') || [];
  const selectedDeviceType = watch('selectedDeviceType');
  const { data, isError, isLoading, isPlaceholderData, refetch } = categoriesQuery;
  const deviceModels = useDeviceModels(selectedDeviceType);
  const { data: devicesData } = useDevicesWithQueryParams({
    page: DEVICE_PAGE_INDEX,
    pageSize: DEVICE_PAGE_SIZE,
    connectionStatus: 'paired',
    models: deviceModels,
  });
  const monitoringDevices: MonitoringDeviceLink[] = selectedDevices.map(
    (selectedDevice) => {
      const deviceDetails = devicesData?.data.find(
        (device) => device.serialNumber === selectedDevice.id,
      );

      return {
        manufacturer: deviceDetails?.manufacturer || 'Eppendorf',
        model: selectedDevice.model || deviceDetails?.model,
        name: deviceDetails?.name || selectedDevice.value || selectedDevice.id,
        serialNumber: selectedDevice.id,
      };
    },
  );
  const selectedCategories = watch('categories') || [];
  const isLoadingCategories = isLoading || isPlaceholderData;

  useEffect(() => {
    if (!data || isPlaceholderData) return;

    const availableCategories = new Map(
      data.data.map((category) => [
        category.key,
        new Set(category.subCategories.map((subCategory) => subCategory.key)),
      ]),
    );
    const reconciledCategories = getValues('categories').flatMap((category) => {
      const availableSubCategories = availableCategories.get(category.key);
      if (!availableSubCategories) return [];

      const subCategories = category.subCategories.filter((subCategory) =>
        availableSubCategories.has(subCategory),
      );
      return subCategories.length ? [{ ...category, subCategories }] : [];
    });

    if (
      JSON.stringify(reconciledCategories) !== JSON.stringify(getValues('categories'))
    ) {
      setValue('categories', reconciledCategories, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [data, getValues, isPlaceholderData, setValue]);

  const toggleSubCategory = (
    categoryKey: DeviceCategory,
    subCategoryKey: DeviceSubCategory,
  ) => {
    const categories = getValues('categories') || [];
    const selectedCategory = categories.find((category) => category.key === categoryKey);
    const isSelected = selectedCategory?.subCategories.includes(subCategoryKey);
    let nextCategories = categories.filter((category) => category.key !== categoryKey);

    if (selectedCategory) {
      const subCategories = isSelected
        ? selectedCategory.subCategories.filter((key) => key !== subCategoryKey)
        : [...selectedCategory.subCategories, subCategoryKey];

      if (subCategories.length) {
        nextCategories = [...nextCategories, { key: categoryKey, subCategories }];
      }
    } else {
      nextCategories = [
        ...nextCategories,
        { key: categoryKey, subCategories: [subCategoryKey] },
      ];
    }

    setValue('categories', nextCategories, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  if (isLoadingCategories) {
    return (
      <div className="condition-screen__state" role="status">
        <DotsLoader />
        <p>{t('tagsCondition.advanced.loading')}</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="condition-screen__state" role="alert">
        <Icon name="error" size={IconSizeClasses.Large} className="bg-red-500" />
        <p className="font-weight-semibold">{t('tagsCondition.advanced.errorTitle')}</p>
        <Button variant="secondary" onClick={() => refetch()}>
          {t('tagsCondition.advanced.retry')}
        </Button>
      </div>
    );
  }

  if (!data?.data.length) {
    const unsupported = Boolean(data?.unsupportedModels?.length);
    return (
      <div className="condition-screen__state">
        <Icon name="no-results" size={IconSizeClasses.Large} className="bg-gray-500" />
        <p className="font-weight-semibold">
          {t(
            unsupported
              ? 'tagsCondition.advanced.unsupportedTitle'
              : 'tagsCondition.advanced.emptyTitle',
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex__dir--column gap-l">
      {Boolean(data.unsupportedModels?.length) && (
        <div className="condition-screen__notice condition-screen__notice--warning">
          <Icon name="warning" size={IconSizeClasses.XSmall} />
          <p>
            {t('tagsCondition.advanced.unsupportedModels', {
              models: data.unsupportedModels?.join(', '),
            })}
          </p>
        </div>
      )}
      {data.data.map((category) => {
        const localizedCategory = localizeDeviceCategory(category, t);
        const selectedSubCategories =
          selectedCategories.find((selected) => selected.key === category.key)
            ?.subCategories || [];

        if (isRangeCategory(category)) {
          const relatedDevices = monitoringDevices.filter(
            (device) =>
              category.appliesToAllModels ||
              !category.applicableModels?.length ||
              !device.model ||
              category.applicableModels.includes(device.model),
          );

          return (
            <NotificationRangeCategoryCard
              key={category.key}
              category={localizedCategory}
              selectedSubCategories={selectedSubCategories}
              monitoringDevices={relatedDevices}
              onToggle={(subCategory) => toggleSubCategory(category.key, subCategory)}
            />
          );
        }

        return (
          <NotificationCategoryCard
            key={category.key}
            category={localizedCategory}
            selectedSubCategories={selectedSubCategories}
            onToggle={(subCategory) => toggleSubCategory(category.key, subCategory)}
          />
        );
      })}
    </div>
  );
}

interface ConditionScreenProps extends AddNotificationRuleScreenProps {
  onBack: () => void;
  onContinue: () => void;
  screenHasErrors: boolean;
}

export function ConditionScreen({
  formControl,
  onBack,
  onContinue,
  screenHasErrors,
}: ConditionScreenProps) {
  const { t } = useTranslation();
  const isCategoryConditionsEnabled = useFeatureIsEnabled(
    'ESEVNLS_4552_DEVICE_EVENT_CATEGORIES',
  );
  const { register, setValue, trigger, watch } = formControl;
  const watchedMode = watch('conditionMode');
  const mode = isCategoryConditionsEnabled
    ? watchedMode || NotificationRuleConditionMode.ADVANCED
    : NotificationRuleConditionMode.BASIC;
  const selectedTags = watch('tags') || [];
  const selectedCategories = watch('categories') || [];
  const selectedDevices = watch('devices') || [];
  const selectedDeviceType = watch('selectedDeviceType');
  const isAdvanced = mode === NotificationRuleConditionMode.ADVANCED;
  const categoriesQuery = useGetDeviceCategoriesList(
    isAdvanced
      ? getDeviceCategoriesQuery(selectedDevices, selectedDeviceType)
      : undefined,
  );

  useEffect(() => {
    if (watchedMode !== mode) {
      setValue('conditionMode', mode);
    }

    register('tags', {
      validate: (tags) =>
        mode !== NotificationRuleConditionMode.BASIC || Boolean(tags?.length),
    });
    register('categories', {
      validate: (categories) =>
        mode !== NotificationRuleConditionMode.ADVANCED ||
        Boolean(categories?.some((category) => category.subCategories.length)),
    });
    trigger(['tags', 'categories']);
  }, [mode, register, setValue, trigger, watchedMode]);

  const handleModeChange = (nextMode: string) => {
    if (
      nextMode !== NotificationRuleConditionMode.BASIC &&
      nextMode !== NotificationRuleConditionMode.ADVANCED
    )
      return;

    setValue('conditionMode', nextMode, {
      shouldDirty: true,
    });
  };

  const hasActiveSelection =
    mode === NotificationRuleConditionMode.BASIC
      ? selectedTags.length > 0
      : selectedCategories.some((category) => category.subCategories.length > 0);
  const isAdvancedUnavailable =
    isAdvanced &&
    (categoriesQuery.isLoading ||
      categoriesQuery.isPlaceholderData ||
      categoriesQuery.isError ||
      !categoriesQuery.data?.data.length);

  return (
    <div className="condition-screen flex flex__dir--column">
      <h1 className="sub-title p-top-xxl p-bottom-xxl">{t('tagsCondition.select')}</h1>

      {isCategoryConditionsEnabled && (
        <CardSelect
          asChild
          value={mode}
          onChange={handleModeChange}
          orientation="horizontal"
          ariaLabel={t('tagsCondition.select')}
        >
          <div className="condition-screen__mode-toggle">
            {[
              NotificationRuleConditionMode.ADVANCED,
              NotificationRuleConditionMode.BASIC,
            ].map((conditionMode) => (
              <CardSelect.Item
                key={conditionMode}
                value={conditionMode}
                className="condition-screen__mode-option"
              >
                <span className="condition-screen__mode-radio" aria-hidden="true" />
                <span className="condition-screen__mode-text">
                  <span className="condition-screen__mode-title">
                    {t(`tagsCondition.modes.${conditionMode}`)}
                  </span>
                  <span className="condition-screen__mode-description">
                    {t(`tagsCondition.modes.${conditionMode}Description`)}
                  </span>
                </span>
              </CardSelect.Item>
            ))}
          </div>
        </CardSelect>
      )}

      <div className="p-bottom-l">
        {isAdvanced ? (
          <AdvancedConditions
            formControl={formControl}
            categoriesQuery={categoriesQuery}
          />
        ) : (
          <SeverityList formControl={formControl} />
        )}
      </div>

      <Dialog.Actions>
        <Button variant="secondary" onClick={onBack}>
          {t('shared.back')}
        </Button>
        <Button
          onClick={onContinue}
          disabled={screenHasErrors || !hasActiveSelection || isAdvancedUnavailable}
        >
          {t('shared.continue')}
        </Button>
      </Dialog.Actions>
    </div>
  );
}
